import { PendingButton } from '../components/common/PendingButton';
import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import type { AvailabilityDay, Period, PortalAvailability, PortalMe, PortalRequestBody, PortalRequestReply } from '@bbbk/schema';
import { api } from '../lib/api';
import { RequestDogsStep } from './RequestDogsStep';
import { RequestDatesStep } from './RequestDatesStep';
import { RequestReviewStep } from './RequestReviewStep';
import { RequestSent } from './RequestSent';
import { BookBrand } from './RequestChrome';
import { LeaveDialog } from './LeaveDialog';
import { ReachFailure } from './ReachFailure';
import type { LockedOutReason } from './CustomerLockedOut';
import {
  addMonths,
  buildRequestBody,
  clearPending,
  failureOf,
  isUntouched,
  keepsSendOnLeave,
  monthEnd,
  monthStart,
  pickDay,
  savePending,
  type DogStayAnswer,
  type StayEnd,
  type PickedRange,
  type RequestFailure,
} from './requestRules';

// The request wizard — flow 2.1, three steps and no money, ending on
// *Booking requested*. This is the part with EFFECTS: the calendar read, the
// send, the retry, the resume. The screens it drives are pure and are pinned
// by the visual contract; the effects are pinned by the Playwright journeys
// (AC21), because a static render cannot see an effect run.

type Step = 1 | 2 | 3 | 'sent' | 'resuming';

function freshId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function blankAnswers(me: PortalMe): Record<string, DogStayAnswer> {
  return Object.fromEntries(me.dogs.map((d) => [d.id, { meds: 'none', instructions: '', keepApart: false }]));
}

function answersFrom(body: PortalRequestBody): Record<string, DogStayAnswer> {
  return Object.fromEntries(
    body.dogs.map((d) => [d.id, { meds: d.meds, instructions: d.instructions ?? '', keepApart: d.keepApart }]),
  );
}

export interface RequestWizardProps {
  me: PortalMe;
  /** A send whose reply never arrived, found on load — sent again with the same id (AC22). */
  resume?: PortalRequestBody | null;
  onLockedOut: (reason: LockedOutReason) => void;
  /** Leave the wizard. `sent` when a request went, so Home re-reads. */
  onDone: (sent: boolean) => void;
  /** The drawer's way of asking to close (a pull, Escape): set to the same `close` the X calls, so it asks first too. */
  closeRef?: MutableRefObject<(() => void) | null>;
}

export function RequestWizard({ me, resume = null, onLockedOut, onDone, closeRef }: RequestWizardProps) {
  const [step, setStep] = useState<Step>(resume ? 'resuming' : 1);
  const [answers, setAnswers] = useState<Record<string, DogStayAnswer>>(() => (resume ? answersFrom(resume) : blankAnswers(me)));
  const [note, setNote] = useState(resume?.note ?? '');
  const [dogsError, setDogsError] = useState<string | null>(null);

  const [month, setMonth] = useState(() => monthStart(resume?.checkIn ?? me.today));
  const [range, setRange] = useState<PickedRange>({ checkIn: resume?.checkIn ?? null, checkOut: resume?.checkOut ?? null });
  const [dropOff, setDropOff] = useState<Period | null>(resume?.dropOff ?? null);
  const [collection, setCollection] = useState<Period | null>(resume?.collection ?? null);
  const [chosenEnd, setChosenEnd] = useState<StayEnd | null>(null);
  const [leaving, setLeaving] = useState(false);

  const [avail, setAvail] = useState<PortalAvailability | null>(null);
  // EVERY MONTH READ, BY ITS FIRST DAY (Freddi, 2026-10-08: "cycling through
  // months causes the calendar to flash"). A day's level does not depend on
  // the range picked — only on the book and the household — so a month read
  // once paints at once when the customer comes back to it.
  const [months, setMonths] = useState<Record<string, AvailabilityDay[]>>({});
  const [loading, setLoading] = useState(false);
  const [calFailure, setCalFailure] = useState(false);
  const [reads, setReads] = useState(0);

  const [sending, setSending] = useState(false);
  const [sendFailure, setSendFailure] = useState<RequestFailure | null>(null);
  const [reply, setReply] = useState<PortalRequestReply | null>(null);
  const body = useRef<PortalRequestBody | null>(resume);

  // ---- the calendar read (AC3, AC23) -------------------------------------
  // One read per change of month, range or times. The answer for an older
  // question never paints over a newer one.
  const gen = useRef(0);
  useEffect(() => {
    if (step !== 2) return;
    const mine = ++gen.current;
    setLoading(true);
    setCalFailure(false);
    const q = new URLSearchParams({ from: month, to: monthEnd(month) });
    if (range.checkIn) q.set('checkIn', range.checkIn);
    if (range.checkOut) q.set('checkOut', range.checkOut);
    if (dropOff) q.set('dropOff', dropOff);
    if (collection) q.set('collection', collection);
    void (async () => {
      try {
        const res = await api<PortalAvailability>(`/portal/availability?${q.toString()}`, { redirectOn401: false, cache: 'reload' });
        setMonths((m) => ({ ...m, [month]: res.data.days }));
        if (mine !== gen.current) return;
        setAvail(res.data);
        // A SUNDAY END IS SET TO THE AFTERNOON BY THE SERVER (AC31); the
        // controls follow what it decided rather than arguing with it.
        const r = res.data.range;
        if (r?.dropOff && r.dropOff !== dropOff) setDropOff(r.dropOff);
        if (r?.collection && r.collection !== collection) setCollection(r.collection);
      } catch (e) {
        if (mine !== gen.current) return;
        const f = failureOf(e);
        if (f === 'signed-out' || f === 'no-household') return onLockedOut(f);
        setCalFailure(true);
      } finally {
        if (mine === gen.current) setLoading(false);
      }
    })();
  }, [step, month, range.checkIn, range.checkOut, dropOff, collection, reads, onLockedOut]);

  // ---- the months either side, read ahead -------------------------------
  // Once the month on screen has its answer, the one after it (and the one
  // before, when that is not in the past) is read quietly, so Next and
  // Previous paint at once. A failure here is silent: the month is read again,
  // visibly, if the customer goes to it.
  const ahead = useRef(new Set<string>());
  // STEP 2 OPENS FULLY DRAWN (Freddi, 2026-10-08: a spinner on every button
  // that waits). The month it opens on is read while the customer answers
  // step 1; *Submit your dogs* waits, busy, only if that read is still out.
  const [opening, setOpening] = useState(false);
  const [firstRead, setFirstRead] = useState<'out' | 'done'>('out');
  useEffect(() => {
    if (step !== 1 || months[month] || ahead.current.has(month)) return;
    ahead.current.add(month);
    void api<PortalAvailability>(`/portal/availability?${new URLSearchParams({ from: month, to: monthEnd(month) }).toString()}`, { redirectOn401: false })
      .then((res) => setMonths((cur) => ({ ...cur, [month]: res.data.days })))
      .catch(() => ahead.current.delete(month))
      .finally(() => setFirstRead('done'));
  }, [step, month, months]);
  // Read or not, a customer waiting on it moves on — step 2 says its own failure.
  useEffect(() => {
    if (opening && (months[month] || firstRead === 'done')) {
      setOpening(false);
      setStep(2);
    }
  }, [opening, months, month, firstRead]);

  useEffect(() => {
    if (step !== 2 || !months[month]) return;
    const near = [addMonths(month, 1), addMonths(month, -1)].filter((m) => m >= monthStart(me.today) && !months[m] && !ahead.current.has(m));
    for (const m of near) {
      ahead.current.add(m);
      void api<PortalAvailability>(`/portal/availability?${new URLSearchParams({ from: m, to: monthEnd(m) }).toString()}`, { redirectOn401: false })
        .then((res) => setMonths((cur) => ({ ...cur, [m]: res.data.days })))
        .catch(() => ahead.current.delete(m));
    }
  }, [step, month, months, me.today]);

  // ---- the send (AC10, AC11, AC22, AC23, AC27) ---------------------------
  const send = async (payload: PortalRequestBody) => {
    body.current = payload;
    // Remembered BEFORE it goes, so a reload after a lost reply sends it again.
    savePending(payload);
    setSending(true);
    setSendFailure(null);
    try {
      const res = await api<PortalRequestReply>('/portal/bookings', { method: 'POST', body: payload, redirectOn401: false, wantResult: true });
      clearPending();
      setReply(res.data);
      setStep('sent');
    } catch (e) {
      const f = failureOf(e);
      if (f === 'signed-out' || f === 'no-household') {
        clearPending();
        return onLockedOut(f);
      }
      if (f === 'refused') {
        // The server judged the dates again and said no (AC11). Nothing was
        // written; step 2 reopens and its notes say why.
        clearPending();
        setStep(2);
        setReads((n) => n + 1);
        return;
      }
      if (f === 'busy') clearPending();
      // `unreachable` keeps the pending body: the write may have landed and
      // the reply been lost, and the retry asks with the same id.
      setSendFailure(f);
      if (step === 'resuming') setStep(3);
    } finally {
      setSending(false);
    }
  };

  // A reply lost before a reload: ask again, ONCE, on mount. The ref holds
  // against React's development double effect, which sent it twice — both
  // answered as replays, but once is what was meant.
  const resumed = useRef(false);
  useEffect(() => {
    if (!resume || resumed.current) return;
    resumed.current = true;
    void send(resume);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // THE WAY OUT (Freddi, 2026-10-08). Nothing entered yet: leave at once. Else
  // ask, because the answers live only in this component and go with it.
  const leave = () => {
    // A send that may have landed stays remembered: the next visit asks again
    // with the same id rather than letting a fresh request book it twice.
    if (!keepsSendOnLeave(sendFailure)) clearPending();
    onDone(false);
  };
  // Not while a send is in flight: the X and Back are disabled then, and the
  // drawer's pull and Escape come through here (#245 review).
  const close = () => { if (sending) return; typeof step === 'number' && isUntouched(step, answers, note) ? leave() : setLeaving(true); };
  if (closeRef) closeRef.current = close;
  const sheet = leaving ? <LeaveDialog onStay={() => setLeaving(false)} onLeave={leave} mayHaveSent={keepsSendOnLeave(sendFailure)} /> : null;

  if (step === 'sent' && reply) return <RequestSent reply={reply} onDone={() => onDone(true)} />;

  if (step === 'resuming') {
    return (
      <main className="cp-app cp-book">
        <BookBrand />
        <div className="cp-check cs-done">
          <span className="cp-spinner" aria-hidden="true" />
          <h1>Sending your request</h1>
          <p className="cp-p mute cs-done-sub">The last one didn’t get an answer — asking again.</p>
        </div>
      </main>
    );
  }

  if (step === 1) {
    return (
      <>
      <RequestDogsStep
        dogs={me.dogs}
        answers={answers}
        note={note}
        today={me.today}
        error={dogsError}
        onChange={(id, a) => {
          setAnswers((cur) => ({ ...cur, [id]: a }));
          setDogsError(null);
        }}
        onNote={setNote}
        onBack={close}
        onClose={close}
        onNext={() => {
          const missing = me.dogs.find((d) => answers[d.id]?.meds === 'yes' && !answers[d.id]!.instructions.trim());
          if (missing) return setDogsError(`Tell us ${missing.name}’s medication — what, how much and when.`);
          if (months[month] || firstRead === 'done') return setStep(2);
          setOpening(true);
        }}
        opening={opening}
      />
      {sheet}
      </>
    );
  }

  if (step === 2) {
    return (
      <>
      <RequestDatesStep
        month={month}
        today={avail?.today ?? me.today}
        days={months[month] ?? null}
        handoverAm={avail?.handoverAm ?? ''}
        handoverPm={avail?.handoverPm ?? ''}
        checkIn={range.checkIn}
        checkOut={range.checkOut}
        dropOff={dropOff}
        collection={collection}
        range={avail?.range ?? null}
        loading={loading}
        failure={calFailure}
        chosenEnd={chosenEnd}
        onChooseEnd={(end) => {
          // A second tap on the end in focus lets it go.
          setChosenEnd((cur) => (cur === end ? null : end));
          // The end's own month, so the day being changed is on screen.
          const day = end === 'dropOff' ? range.checkIn : range.checkOut;
          if (day) setMonth(monthStart(day));
        }}
        onPick={(day) => {
          setRange((cur) => pickDay(cur, day, chosenEnd));
          setChosenEnd(null);
        }}
        onClose={close}
        onMonth={(d) => setMonth((m) => addMonths(m, d))}
        onPeriod={(end, p) => (end === 'dropOff' ? setDropOff(p) : setCollection(p))}
        onBack={() => setStep(1)}
        onRetry={() => setReads((n) => n + 1)}
        onNext={() => {
          // A changed request is a new request: a fresh id each time step 3 opens.
          body.current = null;
          setSendFailure(null);
          setStep(3);
        }}
      />
      {sheet}
      </>
    );
  }

  // Step 3. The quote is the server's, from the last calendar answer.
  const quote = avail?.range?.quote ?? null;
  const payload =
    body.current ??
    (range.checkIn && range.checkOut && dropOff && collection
      ? buildRequestBody({ requestId: freshId(), checkIn: range.checkIn, checkOut: range.checkOut, dropOff, collection, answers, dogIds: me.dogs.map((d) => d.id), note })
      : null);
  if (!payload || !quote) {
    // Reached without an answer to show — a resume that failed before any
    // calendar read. Say so and offer the retry rather than a half ticket.
    return (
      <main className="cp-app cp-book">
        <BookBrand />
        <div className="cp-check cs-done">
          <h1>Your request</h1>
          {sendFailure === 'unreachable' && body.current && <ReachFailure what="send" onRetry={() => void send(body.current!)} busy={sending} />}
          <div className="cp-actions">
            <PendingButton className="cp-btn ghost" onClick={() => { clearPending(); setStep(1); }}>
              Start again
            </PendingButton>
          </div>
        </div>
      </main>
    );
  }
  body.current = payload;
  return (
    <>
    <RequestReviewStep
      dogs={me.dogs}
      answers={answers}
      checkIn={payload.checkIn}
      checkOut={payload.checkOut}
      dropOff={payload.dropOff}
      collection={payload.collection}
      quote={quote}
      sending={sending}
      failure={sendFailure}
      onBack={() => setStep(2)}
      onSend={() => void send(payload)}
      onRetry={() => void send(payload)}
      onClose={close}
    />
    {sheet}
    </>
  );
}
